import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/** PD-16251 — what the saved-price policy did for one item cost change. */
export type ItemCostChangeReceiptAction =
	| 'nothing'
	| 'remove'
	| 'raise'
	| 'lower';

/**
 * PD-16251 — one item cost change the saved-price policy has processed.
 *
 * The ERP integration keeps a change it could not report and sends it again on
 * its next run with the same `changeId`, so the same change can arrive more
 * than once. Applying it twice would raise or lower a saved price twice; this
 * row, written in the same transaction as the saved-price changes, is how a
 * repeat is recognized and skipped.
 *
 * auditable: false — the row IS the record of the change, and every saved
 * price it removed or changed carries its own `_audit` row.
 */
@Index('uk_item_cost_change_receipt_change', ['changeId'], { unique: true })
@Index('idx_item_cost_change_receipt_item', ['fkItemId'])
@Entity('item_cost_change_receipt', {}, { auditable: false })
export class ItemCostChangeReceiptEntity extends BaseEntity {
	/** The integration's id for the change (a UUID). */
	@Column('varchar', { name: 'changeId', length: 64 })
	public changeId!: string;

	@Column('int', { name: 'fk_item__id', unsigned: true })
	public fkItemId!: number;

	@Column('decimal', { name: 'previousCost', precision: 15, scale: 6 })
	public previousCost!: string;

	@Column('decimal', { name: 'newCost', precision: 15, scale: 6 })
	public newCost!: string;

	/** The policy action that applied to the change's direction. */
	@Column('varchar', { name: 'action', length: 16 })
	public action!: ItemCostChangeReceiptAction;

	/** Saved-price rows removed. */
	@Column('int', { name: 'removedCount', unsigned: true, default: 0 })
	public removedCount!: number;

	/** Saved-price rows raised or lowered. */
	@Column('int', { name: 'adjustedCount', unsigned: true, default: 0 })
	public adjustedCount!: number;

	@Column('datetime', { name: 'processedDate' })
	public processedDate!: Date;
}
